/**
 * Comments API — user draft hydration / autosave / submit + comment history.
 *
 * See backend/app/routes/comments.py for the wire shapes.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { HttpError, apiFetch } from './http';

export type CommentKind = 'feature' | 'bug' | 'ui' | null;
export type CommentStatus = 'draft' | 'processing' | 'closed';

export interface CommentDraft {
  id: number | null;
  kind: CommentKind;
  body: string;
  status: CommentStatus;
  updated_at: string | null;
  image_path: string | null;
  submitted_at: string | null;
  processed_at: string | null;
  closed_at: string | null;
  reply_body: string | null;
  response_viewed_at: string | null;
}

export interface PutDraftPayload {
  kind: CommentKind;
  body: string;
}

export function useMyDraft(enabled: boolean) {
  const { getIdToken, user } = useAuth();
  return useQuery<CommentDraft>({
    queryKey: ['comments-me-draft'],
    queryFn: () => apiFetch<CommentDraft>('/api/comments/me/draft', { getIdToken }),
    enabled: enabled && Boolean(user),
    staleTime: 5_000,
  });
}

export function useMyCommentsList(enabled: boolean) {
  const { getIdToken, user } = useAuth();
  return useQuery<CommentDraft[]>({
    queryKey: ['comments-me-list'],
    queryFn: () => apiFetch<CommentDraft[]>('/api/comments/me/list', { getIdToken }),
    enabled: enabled && Boolean(user),
    refetchInterval: enabled ? 5_000 : false,
    staleTime: 0,
  });
}

export function usePutDraft() {
  const { getIdToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PutDraftPayload) =>
      apiFetch<CommentDraft>('/api/comments/me', {
        method: 'PUT',
        body: payload,
        getIdToken,
      }),
    onSuccess: (saved) => {
      // Mirror the saved draft into the cache so the page hydrates with the
      // latest body instead of the stale initial fetch.
      qc.setQueryData<CommentDraft>(['comments-me-draft'], saved);
      void qc.invalidateQueries({ queryKey: ['comments-me-list'] });
    },
  });
}

export function useSubmitDraft() {
  const { getIdToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PutDraftPayload) =>
      apiFetch<CommentDraft>('/api/comments/me/submit', {
        method: 'POST',
        body: payload,
        getIdToken,
      }),
    onSuccess: () => {
      // After submit the returned row is status='processing' and belongs in
      // the history list, not the editable draft form. Reset the draft cache so
      // the user can immediately start a second independent comment.
      qc.setQueryData<CommentDraft>(['comments-me-draft'], {
        id: null,
        kind: null,
        body: '',
        status: 'draft',
        updated_at: null,
        image_path: null,
        submitted_at: null,
        processed_at: null,
        closed_at: null,
        reply_body: null,
        response_viewed_at: null,
      });
      void qc.invalidateQueries({ queryKey: ['comments-me-list'] });
    },
  });
}

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

export function useUploadCommentImage() {
  const { getIdToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<CommentDraft> => {
      const token = await getIdToken();
      const fd = new FormData();
      fd.append('file', file);
      const resp = await fetch(`${API_BASE}/api/comments/me/upload`, {
        method: 'POST',
        body: fd,
        headers: { authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const text = await resp.text();
        let detailCode: string | undefined;
        let detailMessage: string | undefined;
        try {
          const j = JSON.parse(text) as { detail?: { code?: string; message?: string } };
          detailCode = j?.detail?.code;
          detailMessage = j?.detail?.message;
        // intentional swallow: non-JSON body (HTML 5xx page, empty) falls
        // through with undefined code/message — the HttpError still carries
        // the raw bodyText so callers see *something*.
        } catch {
          // (deliberately empty)
        }
        // Throw an HttpError so the modal can key off detailCode (e.g.
        // ERR_COMMENTER_BLOCKED) without re-parsing the body itself.
        throw new HttpError(resp.status, text, undefined, detailCode, detailMessage);
      }
      return (await resp.json()) as CommentDraft;
    },
    onSuccess: (saved) => {
      qc.setQueryData<CommentDraft>(['comments-me-draft'], saved);
      void qc.invalidateQueries({ queryKey: ['comments-me-list'] });
    },
  });
}

export function useRemoveCommentImage() {
  const { getIdToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<CommentDraft>('/api/comments/me/upload', {
        method: 'DELETE',
        getIdToken,
      }),
    onSuccess: (saved) => {
      qc.setQueryData<CommentDraft>(['comments-me-draft'], saved);
      void qc.invalidateQueries({ queryKey: ['comments-me-list'] });
    },
  });
}

export function useMarkCommentResponseViewed() {
  const { getIdToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: number) =>
      apiFetch<CommentDraft>(`/api/comments/me/${commentId}/viewed`, {
        method: 'POST',
        getIdToken,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['comments-me-list'] });
    },
  });
}

/**
 * Fetch an auth-gated upload as a blob URL so we can render it in <img>.
 * objectURLs must be revoked by the caller on unmount/swap to avoid leaks.
 */
export async function fetchCommentImageBlobUrl(
  filename: string,
  getIdToken: () => Promise<string>,
): Promise<string> {
  const token = await getIdToken();
  const resp = await fetch(`${API_BASE}/api/comments/me/upload/${encodeURIComponent(filename)}`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new Error(`image fetch failed: HTTP ${resp.status}`);
  const blob = await resp.blob();
  return URL.createObjectURL(blob);
}
